// Filter category constants
export const FILTER_CATEGORIES = {
  PRICE: 'price',
  COUNTRY_PLACE_OF_ORIGIN: 'country/place of origin',
}

// Filter category mappings for analytics
export const FILTER_CATEGORY_MAPPINGS = {
  [FILTER_CATEGORIES.COUNTRY_PLACE_OF_ORIGIN]: 'origin',
}

interface SelectedFilter {
  label: string
  category: string
  checked: boolean
}

export interface CheckoutAddress {
  address: string
  storeId: number
  id: number
  pincode: string
  storeName: string
  storeType: string
  availableStores: Array<{
    address: string
    clientId: string
    hasClickCollect: boolean
    hasExpressDelivery: boolean
    hasPicking: boolean
    hasSelfCheckout: boolean
    id: number
    latitude: number
    longitude: number
    name: string
    pickingStoreId: number | null
    status: string
    storeType: string
  }>
  location: {
    latitude: string
    longitude: string
  }
  pickingStoreId: number | null
  type: string
  building: string
  clientId: string
}

interface FilterAppliedEventParams {
  FILTER_CATEGORY: string
  FILTER_DETAIL: string
  STORE_ID_SAP: string | null
  STORE_ID_DBP: string | null
}

/**
 * Formats a price filter string for analytics tracking by handling special cases
 * like low-end prices starting from 0 and unlimited high-end price ranges.
 *
 * @param {string} priceString - ex. '$10 - $25' or '<$3' or '>$25'
 * @returns {string} The formatted price range string ex. '* to 3' or '25 to *' or '10 to 25'
 */
const formatPriceFilter = (priceString: string): string => {
  // Handle '<$3' or '>$25' cases
  if (priceString.startsWith('<')) {
    return `* to ${priceString.replace('<$', '')}`
  }
  if (priceString.startsWith('>')) {
    return `${priceString.replace('>$', '')} to *`
  }

  // Handle range cases like '$10 - $25'
  const priceRange = priceString.replaceAll(/[[\]'$]+/g, '')
  if (priceRange.includes(' - ')) {
    const [minPrice, maxPrice] = priceRange.split(' - ')
    const min = Number.parseFloat(minPrice)
    const max = Number.parseFloat(maxPrice)

    if (min === 0 && max <= 3) {
      return `* to ${maxPrice}`
    }
    if (min >= 25 && maxPrice === '*') {
      return `${minPrice} to *`
    }

    return `${minPrice} to ${maxPrice}`
  }

  return priceString
}

export const createFilterAppliedEventParams = (
  selectedFilter: SelectedFilter,
  checkoutAddress: CheckoutAddress
): FilterAppliedEventParams => {
  const { label = '', category = '' } = selectedFilter || {}

  let filterDetail = label.toLowerCase()
  let filterCategory = category.toLowerCase()

  // Handle special price formatting
  if (category === FILTER_CATEGORIES.PRICE) {
    filterDetail = formatPriceFilter(label)
  }

  // Handle category name mapping
  if (filterCategory === FILTER_CATEGORIES.COUNTRY_PLACE_OF_ORIGIN) {
    filterCategory = FILTER_CATEGORY_MAPPINGS[FILTER_CATEGORIES.COUNTRY_PLACE_OF_ORIGIN]
  }

  return {
    FILTER_CATEGORY: filterCategory,
    FILTER_DETAIL: filterDetail,
    STORE_ID_DBP: checkoutAddress?.storeId?.toString() || null,
    STORE_ID_SAP: checkoutAddress?.clientId?.toString() || null,
  }
}
